import { atom } from "jotai";
import { sharedGalaxiesApi, type SharedGalaxyView } from "../api/client";

// ── Shared Galaxies (viewer) store ──
//
// The galaxies an owner shared read-only WITH this viewer — the GalaxySwitcher's
// third "Shared" category (docs/design/shared-galaxies.md). Mirrors the
// affiliations (archive) store: a shared atom + an explicit refresh action, so any
// mutation site — claiming a new link, leaving one — has ONE place to invalidate,
// and the switcher never needs a full reload to reflect a change.

export const sharedGalaxiesAtom = atom<SharedGalaxyView[]>([]);

// Has the list been fetched at least once this session? Distinguishes "none
// shared" from "not loaded yet" so the switcher lazy-loads on first open.
export const sharedGalaxiesLoadedAtom = atom<boolean>(false);

// Serialize refreshes so a claim/leave triggered while a switcher-open fetch is in
// flight isn't raced out by the older result — the newest call always wins the
// last write (same discipline as refreshAffiliationsAtom).
let tail: Promise<void> | null = null;

// refreshSharedGalaxiesAtom (re)fetches the shared-with-me list and is the ONLY
// way to mutate sharedGalaxiesAtom. Call it after claim/leave, and lazily on first
// switcher open. Errors are swallowed (an unreachable list simply hides the
// section) but still mark loaded so the lazy effect doesn't hammer.
export const refreshSharedGalaxiesAtom = atom(null, (_get, set) => {
  const run = () =>
    sharedGalaxiesApi
      .sharedWithMe()
      .then((res) => {
        set(sharedGalaxiesAtom, res.galaxies || []);
      })
      .catch((e) => {
        console.error("shared galaxies refresh failed", e);
      })
      .finally(() => {
        set(sharedGalaxiesLoadedAtom, true);
      });
  const next = tail ? tail.then(run, run) : run();
  tail = next.finally(() => {
    if (tail === next) tail = null;
  });
  return next;
});
